// "Bağlantı" sekmesi — SCADA tarafına verilecek HER ŞEYİN tek okunduğu yer.
//
// Neden tek blok: bu ekranı kullanan kişi çoğu zaman BTYS'yi değil, karşı
// taraftaki ajanı kuruyor. Ona lazım olan altı değer (adres · yöntem · başlık ·
// site · page.id · anahtar) daha önce panelin dört ayrı bölümüne dağılmıştı;
// "neyi nereden okuyacağım" sorusunun tek cevabı bu sekme. Örnek paket ve
// sınama komutu da buradan kopyalanıyor, böylece kurulum yaparken belgeye
// (docs/ENTEGRASYON.md) bakmak zorunda kalmıyor.
//
// Değerler ekranda uydurulmuyor: adres sunucudan (`ucNoktaUrl`), site kaynak
// kodundan, page.id sayfa kodundan geliyor. Anahtar ise kasadan çözülüp
// gösterilebiliyor (AnahtarKasasi.cs) — gösterilemiyorsa yerine yer tutucu
// konur ve neden gösterilemediği yazılır.

import { useEffect, useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { toast } from '../../components/Toast'
import { ApiHatasi } from '../../lib/api'
import { KopyaMini } from './Kopyala'
import { entegrasyon, type AnahtarSatiri } from './api'

/** Anahtar gösterilemediğinde örneklerde bunun yerini alır. */
const YER_TUTUCU = '<API-ANAHTARINIZ>'

/** Sözleşme satırı: alan · değer (kopyalanabilir) · ne işe yaradığı. */
function Satir({
  alan,
  deger,
  not,
  kopya,
  vurgu,
}: {
  alan: string
  deger: React.ReactNode
  not: string
  /** Kopyalanacak düz metin; yoksa kopyalama düğmesi çıkmaz. */
  kopya?: string | null
  vurgu?: boolean
}) {
  return (
    <tr>
      <td style={{ whiteSpace: 'nowrap' }}>
        <span className="kod">{alan}</span>
      </td>
      <td>
        <div
          className="mono"
          style={{
            fontSize: 11.5,
            fontWeight: vurgu ? 600 : 400,
            color: vurgu ? 'var(--navy-700)' : undefined,
            overflowWrap: 'anywhere',
          }}
        >
          {deger}
        </div>
      </td>
      <td className="cell-sub" style={{ fontSize: 10.5 }}>
        {not}
      </td>
      <td style={{ width: 30 }}>
        {kopya ? <KopyaMini metin={kopya} etiket={alan} /> : null}
      </td>
    </tr>
  )
}

export default function Baglanti({
  url,
  saha,
  sayfaKodu,
  anahtarlar,
  yazabilir,
  yeniAnahtar,
}: {
  url: string
  /** Zarftaki `site` — kaynağın kodu. */
  saha: string
  /** Zarftaki `page.id`; kaynak panelinde seçilmemiş olabilir. */
  sayfaKodu: string | null
  /** Bu kapsamda geçerli anahtarlar (iptal edilmişler de listede, seçilemez). */
  anahtarlar: AnahtarSatiri[]
  yazabilir: boolean
  yeniAnahtar: () => void
}) {
  const etkin = useMemo(() => anahtarlar.filter((a) => a.durum === 'aktif'), [anahtarlar])
  const [secimId, setSecimId] = useState<string | null>(null)
  const [duzMetin, setDuzMetin] = useState<string | null>(null)

  // Seçim etkin anahtarlara bağlı: anahtar iptal edilir ya da yenisi üretilirse
  // ekran elinde kalmış bir kimliği göstermeye devam etmesin.
  useEffect(() => {
    setSecimId((s) => (s && etkin.some((a) => a.id === s) ? s : (etkin[0]?.id ?? null)))
  }, [etkin])

  useEffect(() => {
    setDuzMetin(null)
  }, [secimId])

  const secili = etkin.find((a) => a.id === secimId) ?? null

  const goster = useMutation({
    mutationFn: (id: string) => entegrasyon.anahtarGoster(id),
    onSuccess: (g) => {
      if (g.duzMetin) setDuzMetin(g.duzMetin)
      else toast(g.not ?? 'Bu anahtarın düz metni saklanmamış.')
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Anahtar gösterilemedi'),
  })

  const anahtarMetni = duzMetin ?? (secili ? `${secili.onek}…` : YER_TUTUCU)
  const gercek = duzMetin ?? YER_TUTUCU
  const basligiTam = `Bearer ${gercek}`



  return (
    <>
      {etkin.length === 0 && (
        <div className="info-box">
          <b>Etkin anahtar yok.</b>
          <span>
            Ajan bu kapsama paket gönderemez — gönderdiği her paket 401 alır ve sözleşme
            gereği bir daha denenmez. Aşağıdan bir anahtar üretin.
          </span>
        </div>
      )}

      <div className="sect-t">İstek Sözleşmesi</div>
      <table className="grid">
        <thead>
          <tr>
            <th style={{ width: 128 }}>Alan</th>
            <th>Değer</th>
            <th style={{ width: '38%' }}>Açıklama</th>
            <th style={{ width: 30 }} />
          </tr>
        </thead>
        <tbody>
          <Satir alan="Yöntem" deger="POST" not="Gövde TEK paket JSON'u — dizi değil" />
          <Satir
            alan="URL"
            deger={url}
            not="Ajanda agent.post.url; adres yanlışsa paketler ölü mektuba düşer"
            kopya={url}
            vurgu
          />
          <Satir
            alan="Authorization"
            deger={
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                <span>Bearer {anahtarMetni}</span>
                {/*
                  Anahtar varsayılan olarak KAPALI; göz düğmesiyle açılıyor.
                  Kullanıcı isteği: değer ekranın tepesinde büyük puntoyla
                  sürekli durmasın, yalnız bu satırda ve istendiğinde görünsün.
                */}
                <button
                  className="mini-btn"
                  type="button"
                  disabled={!secili || !yazabilir || !secili.gosterilebilir || goster.isPending}
                  title={
                    !secili
                      ? 'Etkin anahtar yok — önce anahtar üretin'
                      : !secili.gosterilebilir
                        ? 'Bu anahtar kasa eklenmeden önce üretilmiş — geri getirilemez'
                        : !yazabilir
                          ? 'Anahtarı görmek için yazma yetkisi gerekiyor'
                          : duzMetin
                            ? 'Gizle'
                            : 'Anahtarı göster'
                  }
                  onClick={() =>
                    duzMetin ? setDuzMetin(null) : secili && goster.mutate(secili.id)
                  }
                >
                  {duzMetin ? (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19M1 1l22 22" />
                    </svg>
                  ) : (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
                {etkin.length > 1 && (
                  <select
                    className="in mono"
                    style={{ fontSize: 10.5, padding: '1px 4px', borderRadius: 5, maxWidth: 150 }}
                    value={secimId ?? ''}
                    onChange={(e) => setSecimId(e.target.value)}
                    title="Bu kaynakta birden çok etkin anahtar var — örnekler seçilene göre yazılır"
                  >
                    {etkin.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.ad}
                        {a.sayfaKodu ? ` · ${a.sayfaKodu}` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </span>
            }
            not="Tek kimlik başlığı — başka başlık ya da çerez okunmaz; anahtar kaynağın tamamına geçerli"
            kopya={duzMetin ? basligiTam : null}
            vurgu
          />
          <Satir
            alan="Content-Type"
            deger="application/json; charset=utf-8"
            not="Sabit"
            kopya="application/json; charset=utf-8"
          />
          <Satir
            alan="site"
            deger={saha}
            not="Zarftaki saha kimliği — paketi bu kaynağa bağlar"
            kopya={saha}
            vurgu
          />
          <Satir
            alan="page.id"
            deger={sayfaKodu ?? 'ajanın konfigürasyonundaki sayfa kimliği'}
            not={
              sayfaKodu
                ? 'Zarftaki sayfa kimliği — paketi bu sayfaya bağlar'
                : 'Tanınmayan kod ilk pakette kendiliğinden sayfa açar'
            }
            kopya={sayfaKodu}
            vurgu={!!sayfaKodu}
          />
          <Satir
            alan="seq"
            deger="monoton tam sayı (64-bit)"
            not="Tekilleştirme anahtarı (site, seq) — aynı çift ikinci kez yazılmaz"
          />
          <Satir
            alan="captured_at"
            deger="ISO-8601 UTC · 7 haneli kesir"
            not="Okumaların zaman damgası budur; alınma damgasını sunucu ayrıca atar"
          />
          <Satir
            alan="Sınırlar"
            deger="≤ 64 KB · 10 sn"
            not="Ajan yanıtı 10 sn'den uzun beklemez; gzip yok"
          />
        </tbody>
      </table>

      <div className="rule">
        <b>Yanıt</b>
        <span>
          2xx → ajan paketi kuyruğundan siler · 408/429/5xx → tekrar dener, sıra bozulmaz ·
          diğer 4xx → paket ölü mektuba düşer ve <b>bir daha gönderilmez</b>.
        </span>
      </div>

      <div style={{ padding: '10px 12px 12px' }}>
        <button
          className="btn btn-sec btn-sm"
          type="button"
          disabled={!yazabilir}
          title={yazabilir ? 'Bu kapsam için yeni API anahtarı üret' : 'Bu modülde yazma yetkiniz yok'}
          onClick={yeniAnahtar}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 5v14M5 12h14" />
          </svg>
          Yeni anahtar
        </button>
      </div>

    </>
  )
}
